/**
 * Garde-fou de crash (T3, polish alpha) — avant : une exception non
 * gérée laissait un écran figé sans explication (Sentry logge en prod,
 * mais l'utilisateur ne voit rien).
 *
 * Écoute `error` + `unhandledrejection` au niveau window et affiche
 * UNE fois un bandeau bienveillant avec un bouton « Recharger ». DOM
 * pur (aucune dépendance lit-html : si le rendu est cassé, le bandeau
 * doit survivre). Ton positif (CLAUDE.md §2.1), libellés i18n avec
 * fallback FR si le module i18n est lui-même en cause.
 */
import { t } from '../i18n/index.js';

let shown = false;

function showCrashBanner(): void {
  if (shown || typeof document === 'undefined') return;
  shown = true;

  let title = 'Oups, quelque chose s’est emmêlé.';
  let action = 'Recharger';
  try {
    title = t('error_boot_title');
    action = t('error_boot_reload');
  } catch {
    // i18n indisponible — fallback FR ci-dessus.
  }

  const banner = document.createElement('div');
  banner.setAttribute('data-testid', 'crash-banner');
  banner.setAttribute('role', 'alert');
  banner.style.cssText =
    'position:fixed;left:16px;right:16px;bottom:16px;z-index:9999;' +
    'background:#3F4A44;color:#fff;border-radius:12px;padding:14px 18px;' +
    'display:flex;align-items:center;gap:12px;font-family:Lato,sans-serif;' +
    'box-shadow:0 8px 24px rgba(0,0,0,.25);font-size:14px;';

  const text = document.createElement('span');
  text.textContent = title;
  text.style.cssText = 'flex:1;min-width:0;';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = action;
  btn.style.cssText =
    'background:#fff;color:#3F4A44;border:0;border-radius:8px;' +
    'padding:8px 14px;font-weight:700;cursor:pointer;font-family:inherit;';
  btn.addEventListener('click', () => window.location.reload());

  banner.append(text, btn);
  document.body.appendChild(banner);
}

/** Installe les handlers globaux. Retourne un uninstall (tests/HMR). */
export function installCrashGuard(): () => void {
  const onError = (): void => showCrashBanner();
  const onRejection = (): void => showCrashBanner();
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/** @internal pour tests. */
export function _resetCrashGuardForTests(): void {
  shown = false;
  document.querySelector('[data-testid="crash-banner"]')?.remove();
}
